import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import type { Components } from "react-markdown";
import "katex/dist/katex.min.css";
import { cn } from "../../lib/cn";

interface MarkdownPreviewProps {
  content: string;
  className?: string;
}

const markdownComponents: Components = {
  h1: ({ children, ...props }) => (
    <h1
      style={{
        fontFamily: "var(--font-display)",
        fontSize: "2rem",
        fontWeight: 700,
        lineHeight: 1.3,
        marginTop: "2rem",
        marginBottom: "1rem",
        color: "var(--text-primary)",
        letterSpacing: "-0.01em",
      }}
      {...props}
    >
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2
      style={{
        fontFamily: "var(--font-display)",
        fontSize: "1.6rem",
        fontWeight: 600,
        lineHeight: 1.35,
        marginTop: "1.75rem",
        marginBottom: "0.75rem",
        color: "var(--text-primary)",
        letterSpacing: "-0.005em",
      }}
      {...props}
    >
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3
      style={{
        fontFamily: "var(--font-display)",
        fontSize: "1.3rem",
        fontWeight: 600,
        lineHeight: 1.4,
        marginTop: "1.5rem",
        marginBottom: "0.5rem",
        color: "var(--text-primary)",
      }}
      {...props}
    >
      {children}
    </h3>
  ),
  h4: ({ children, ...props }) => (
    <h4
      style={{
        fontFamily: "var(--font-display)",
        fontSize: "1.1rem",
        fontWeight: 600,
        lineHeight: 1.45,
        marginTop: "1.25rem",
        marginBottom: "0.5rem",
        color: "var(--text-primary)",
      }}
      {...props}
    >
      {children}
    </h4>
  ),
  h5: ({ children, ...props }) => (
    <h5
      style={{
        fontFamily: "var(--font-display)",
        fontSize: "1rem",
        fontWeight: 600,
        lineHeight: 1.5,
        marginTop: "1rem",
        marginBottom: "0.4rem",
        color: "var(--text-primary)",
      }}
      {...props}
    >
      {children}
    </h5>
  ),
  h6: ({ children, ...props }) => (
    <h6
      style={{
        fontFamily: "var(--font-display)",
        fontSize: "0.9rem",
        fontWeight: 600,
        lineHeight: 1.5,
        marginTop: "1rem",
        marginBottom: "0.4rem",
        color: "var(--text-secondary)",
      }}
      {...props}
    >
      {children}
    </h6>
  ),
  p: ({ children, ...props }) => (
    <p
      style={{
        marginBottom: "1rem",
        lineHeight: 1.75,
        color: "var(--text-primary)",
      }}
      {...props}
    >
      {children}
    </p>
  ),
  a: ({ children, href, ...props }) => {
    const unsafeScheme = /^\s*(javascript|data|vbscript):/i;
    const safeHref = href && unsafeScheme.test(href) ? undefined : href;
    return (
    <a
      href={safeHref}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: "var(--color-cool-azure)",
        textDecoration: "none",
        borderBottom: "1px solid transparent",
        transition: `border-color var(--duration-fast) var(--easing-default)`,
      }}
      onMouseEnter={(e) => {
        (e.target as HTMLElement).style.borderBottomColor = "var(--color-cool-azure)";
      }}
      onMouseLeave={(e) => {
        (e.target as HTMLElement).style.borderBottomColor = "transparent";
      }}
      {...props}
    >
      {children}
    </a>
    );
  },
  code: ({ children, className: codeClassName, ...props }) => {
    const isInline = !codeClassName;
    if (isInline) {
      return (
        <code
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.875em",
            padding: "0.15em 0.4em",
            borderRadius: "var(--radius-sm)",
            background: "var(--bg-secondary)",
            border: "1px solid var(--border-hairline)",
            color: "var(--text-primary)",
          }}
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code
        className={codeClassName}
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "0.85rem",
          lineHeight: 1.6,
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }) => (
    <pre
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: "0.85rem",
        lineHeight: 1.6,
        padding: "1rem 1.25rem",
        borderRadius: "var(--radius-lg)",
        background: "var(--bg-secondary)",
        border: "1px solid var(--border-hairline)",
        overflowX: "auto",
        marginBottom: "1rem",
      }}
      {...props}
    >
      {children}
    </pre>
  ),
  blockquote: ({ children, ...props }) => (
    <blockquote
      style={{
        borderLeft: "3px solid var(--color-lever-peach)",
        paddingLeft: "1rem",
        marginLeft: 0,
        marginBottom: "1rem",
        fontStyle: "italic",
        color: "var(--text-secondary)",
      }}
      {...props}
    >
      {children}
    </blockquote>
  ),
  table: ({ children, ...props }) => (
    <div style={{ overflowX: "auto", marginBottom: "1rem" }}>
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontFamily: "var(--font-sans)",
          fontSize: "0.9rem",
        }}
        {...props}
      >
        {children}
      </table>
    </div>
  ),
  th: ({ children, ...props }) => (
    <th
      style={{
        padding: "0.6rem 0.75rem",
        textAlign: "left",
        fontWeight: 600,
        borderBottom: "2px solid var(--border-default)",
        color: "var(--text-primary)",
        fontSize: "0.85rem",
        letterSpacing: "0.02em",
      }}
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }) => (
    <td
      style={{
        padding: "0.5rem 0.75rem",
        borderBottom: "1px solid var(--border-hairline)",
        color: "var(--text-primary)",
      }}
      {...props}
    >
      {children}
    </td>
  ),
  ul: ({ children, ...props }) => (
    <ul
      style={{
        paddingLeft: "1.5rem",
        marginBottom: "1rem",
        lineHeight: 1.75,
        listStyleType: "disc",
      }}
      {...props}
    >
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol
      style={{
        paddingLeft: "1.5rem",
        marginBottom: "1rem",
        lineHeight: 1.75,
        listStyleType: "decimal",
      }}
      {...props}
    >
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li
      style={{
        marginBottom: "0.25rem",
        color: "var(--text-primary)",
      }}
      {...props}
    >
      {children}
    </li>
  ),
  hr: (props) => (
    <hr
      style={{
        border: "none",
        borderTop: "1px solid var(--border-subtle)",
        margin: "2rem 0",
      }}
      {...props}
    />
  ),
  img: ({ alt, ...props }) => (
    <img
      alt={alt}
      style={{
        maxWidth: "100%",
        borderRadius: "var(--radius-md)",
        marginBottom: "1rem",
      }}
      {...props}
    />
  ),
};

export function MarkdownPreview({ content, className }: MarkdownPreviewProps) {
  return (
    <div
      className={cn("h-full overflow-auto", className)}
      style={{
        fontFamily: "var(--font-display)",
        lineHeight: 1.75,
        color: "var(--text-primary)",
      }}
    >
      <div className="max-w-3xl mx-auto px-8 py-8">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkMath]}
          rehypePlugins={[[rehypeKatex, { strict: false }]]}
          components={markdownComponents}
        >
          {content}
        </ReactMarkdown>
      </div>
    </div>
  );
}
